import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useTitle } from 'react-use';

import { integrationsEndpoints as endpoints } from '@/api/endpoints';
import IntegrationsHeader from '@/components/organisms/Headers/IntegrationsHeader';
import IntegrationCustom from '@/components/organisms/Integrations/Custom/Custom';
import IntegrationEmail from '@/components/organisms/Integrations/Email/Email';
import IntegrationFacebook from '@/components/organisms/Integrations/Facebook/Facebook';
import IntegrationFront from '@/components/organisms/Integrations/Front/Front';
import IntegrationInfobipRcs from '@/components/organisms/Integrations/InfobipRcs/InfobipRcs';
import IntegrationInfobipSms from '@/components/organisms/Integrations/InfobipSms/InfobipSms';
import IntegrationSkeleton from '@/components/organisms/Integrations/IntegrationSkeleton';
import IntegrationIntercom from '@/components/organisms/Integrations/Intercom/Intercom';
import IntegrationLiveChat from '@/components/organisms/Integrations/LiveChat/LiveChat';
import IntegrationMstatViber from '@/components/organisms/Integrations/MstatViber/MstatViber';
import IntegrationSlack from '@/components/organisms/Integrations/Slack/Slack';
import IntegrationSmartlead from '@/components/organisms/Integrations/Smartlead/Smartlead';
import IntegrationSunco from '@/components/organisms/Integrations/Sunco/Sunco';
import IntegrationTelnyxVoice from '@/components/organisms/Integrations/TelnyxVoice/TelnyxVoice';
import IntegrationTwilioVoice from '@/components/organisms/Integrations/TwilioVoice/TwilioVoice';
import IntegrationViber from '@/components/organisms/Integrations/Viber/Viber';
import IntegrationWebWidget from '@/components/organisms/Integrations/Web/Web';
import IntegrationWebVoice from '@/components/organisms/Integrations/WebVoice/WebVoice';
import IntegrationWhatsapp from '@/components/organisms/Integrations/Whatsapp';
import IntegrationZendesk from '@/components/organisms/Integrations/Zendesk/Zendesk';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { useIntegrations } from '@/hooks/useIntegrations';
import { getPermissions } from '@/redux/permissions/selectors';
import { setIntegrationId } from '@/redux/session/actions';
import useSegments from '@/segment/useSegments';
import { IntegrationType } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { getChannelKey } from '@/util/channelName';

import { getIntegrationFormDefaults, integrationsSchema } from './schemas';

const getIntegrationPageTitle = (
  t: TFunction,
  integrationType: IntegrationType | undefined,
  deskName: string | undefined
): string => {
  const channel = integrationType ? t(getChannelKey(integrationType)) : t('pages.integration');
  return `${channel} | ${deskName ?? '-'}`;
};

const Integration = () => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const { integrationId } = useParams();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  useSegments(PageName.INTEGRATION);

  useEffect(() => {
    dispatch(setIntegrationId(integrationId));
    return () => {
      dispatch(setIntegrationId(null));
    };
  }, [dispatch, integrationId]);

  const { integration, verifyIntegration } = useIntegrations(deskId, integrationId);

  // Initialize form for integration configuration with proper type-specific defaults
  type IntegrationFormValues = ReturnType<typeof getIntegrationFormDefaults>;
  const formMethods = useForm<IntegrationFormValues>({
    defaultValues: getIntegrationFormDefaults(integration),
    mode: 'onChange',
    resolver: yupResolver<IntegrationFormValues, unknown, IntegrationFormValues>(
      integrationsSchema
    ),
  });

  // Update form values when integration data changes
  useEffect(() => {
    if (integration) {
      const defaults = getIntegrationFormDefaults(integration);
      formMethods.reset(defaults);
    }
  }, [integration, formMethods]);

  const { desks } = useDesks();
  const desk = desks?.find((desk) => desk.desk_id === deskId);

  useTitle(getIntegrationPageTitle(t, integration?.type, desk?.name));

  useEffect(() => {
    const queryKey = [`${endpoints.integration(deskId, integrationId)}/verify`];
    if (
      !queryClient.getQueryData(queryKey) &&
      canWrite &&
      integration?.type &&
      integration?.active &&
      integration?.type !== 'web'
    ) {
      verifyIntegration();
    }
  }, [
    canWrite,
    deskId,
    integration?.active,
    integration?.type,
    integrationId,
    queryClient,
    verifyIntegration,
  ]);

  const selectIntegrationComponent = () => {
    switch (integration?.type) {
      case 'facebook':
      case 'instagram':
        return <IntegrationFacebook />;
      case 'viber':
        return <IntegrationViber />;
      case 'mstat-viber':
        return <IntegrationMstatViber />;
      case 'web':
        return <IntegrationWebWidget />;
      case 'zendesk':
        return <IntegrationZendesk />;
      case 'intercom':
        return <IntegrationIntercom />;
      case 'livechat':
        return <IntegrationLiveChat />;
      case 'whatsapp':
        return <IntegrationWhatsapp />;
      case 'sunco':
        return <IntegrationSunco />;
      case 'front':
        return <IntegrationFront />;
      case 'infobip-sms':
        return <IntegrationInfobipSms />;
      case 'infobip-rcs':
        return <IntegrationInfobipRcs />;
      case 'smartlead':
        return <IntegrationSmartlead />;
      case 'twilio-voice':
        return <IntegrationTwilioVoice />;
      case 'telnyx-voice':
        return <IntegrationTelnyxVoice />;
      case 'web-voice':
        return <IntegrationWebVoice />;
      case 'email':
        return <IntegrationEmail />;
      case 'slack':
        return <IntegrationSlack />;
      case 'custom':
        return <IntegrationCustom />;
      default:
        return <IntegrationSkeleton />;
    }
  };

  return (
    <FormProvider {...formMethods}>
      <IntegrationsHeader />
      <PageContentWrapper gutters readOnly={!canWrite} className="[overflow-anchor:none]">
        {integration ? (
          <div className="mx-auto w-fit">{selectIntegrationComponent()}</div>
        ) : (
          <IntegrationSkeleton />
        )}
      </PageContentWrapper>
    </FormProvider>
  );
};

export default Integration;
